import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {containsWritingCoachContent} from "@khan/assignments-utils";
import type {Assignment} from "@khan/class-types";

import {contentArticle, contentExercise, contentVideo} from "../../icons.ts";

import khanmigoActivityIcon from "../khanmigo-activity-icon.svg";
import externalLinkIcon from "@phosphor-icons/core/regular/arrow-square-out.svg";

export const getContentKindLabelMap = () => ({
    AIGuideActivity: t`Khanmigo Activity`,
    Exercise: t`Exercise`,
    Article: t`Article`,
    Video: t`Video`,
    TopicUnitTest: t`Topic unit test`,
    TopicQuiz: t`Topic quiz`,
    Scratchpad: t`Scratchpad`,
    Challenge: t`Challenge`,
    Interactive: t`Interactive`,
    Project: t`Project`,
    Talkthrough: t`Talkthrough`,
    CourseChallenge: t`Course Challenge`,
});

const contentKindIconMap = {
    AIGuideActivity: khanmigoActivityIcon,
    Exercise: contentExercise,
    Article: contentArticle,
    Video: contentVideo,
    TopicUnitTest: contentExercise,
    TopicQuiz: contentExercise,
    Scratchpad: contentExercise,
    Challenge: contentExercise,
    Interactive: contentExercise,
    Project: contentExercise,
    Talkthrough: contentExercise,
    CourseChallenge: contentExercise,
} as const;

// Writing Coach assignments have reports,
// but AIGuideActivity content kind does not in general.
// Thus, we only broadly show the report link for the following content kinds,
// and we inline the condition to show the report for Writing Coach content.
const ReportAvailableContentKinds = [
    "Exercise",
    "TopicQuiz",
    "TopicUnitTest",
    "CourseChallenge",
];

type Props = {
    assignment?: Assignment;
    isChildAssignment: boolean;
};

class AssignmentProgressHeaderCell extends React.PureComponent<Props> {
    render(): null | React.ReactElement<React.ComponentProps<"div">> {
        const {assignment, isChildAssignment} = this.props;

        if (!assignment) {
            return null;
        }

        const {id, kind, translatedTitle} = assignment.contents[0];
        const contentKindIcon = contentKindIconMap[kind];
        const contentKindLabelMap = getContentKindLabelMap();
        const contentKindLabel = contentKindLabelMap[kind];
        const isWritingCoachAssignment = containsWritingCoachContent([
            `${kind}:${id}`,
        ]);
        const reportLink = `/teacher/report/assignment/${assignment.id}`;

        return (
            <div className={css(styles.assignmentHeaderCell)}>
                <div
                    className={css(styles.assignmentHeaderCellIcon)}
                    aria-label={`Content kind ${contentKindLabel}`}
                    title={contentKindLabel}
                >
                    {kind === "AIGuideActivity" ? (
                        <PhosphorIcon
                            size="medium"
                            icon={contentKindIcon}
                            color="currentColor"
                        />
                    ) : (
                        <PhosphorIcon icon={contentKindIcon} size="medium" />
                    )}
                </div>

                <div className={css(styles.assignmentHeaderCellWrapper)}>
                    <div
                        className={css(styles.assignmentHeaderCellTitle)}
                        aria-label={
                            assignment.title
                                ? assignment.title
                                : translatedTitle
                        }
                        data-testid="assignment-progress-header-label"
                    >
                        {ReportAvailableContentKinds.includes(kind) &&
                        !isChildAssignment ? (
                            <Link href={reportLink}>{translatedTitle}</Link>
                        ) : kind === "AIGuideActivity" && assignment.title ? (
                            isWritingCoachAssignment ? (
                                <Link
                                    href={`/writing-coach/assignment/${assignment.id}`}
                                    target="_blank"
                                    endIcon={
                                        <PhosphorIcon icon={externalLinkIcon} />
                                    }
                                >
                                    {assignment.title}
                                </Link>
                            ) : (
                                assignment.title
                            )
                        ) : (
                            translatedTitle
                        )}
                    </div>
                    <div
                        className={css(styles.assignmentHeaderCellDueDate)}
                        data-testid="assignment-progress-header-due-date"
                    >
                        {moment(assignment.dueDate).format("MMM D")}
                    </div>
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    assignmentHeaderCell: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        fontSize: 10,
        padding: 8,
        paddingRight: 0,
    },
    assignmentHeaderCellIcon: {
        height: 24,
        width: "100%",
        color: color.offBlack64,
        paddingBottom: 4,
    },
    assignmentHeaderCellWrapper: {
        display: "flex",
        flexDirection: "column",
    },
    assignmentHeaderCellTitle: {
        overflow: "hidden",
        lineHeight: "1.2",
        alignSelf: "flex-end",
    },
    assignmentHeaderCellDueDate: {
        width: "100%",
        paddingTop: 4,
        color: color.offBlack64,
    },
});

export default AssignmentProgressHeaderCell;
